import { TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
import {
  ActivityList,
  AssetsStatisticsPart,
  HighMediumLowList,
  HolderTaxAgentList,
  HolderTaxAgentType,
  InformationDisclosureList,
  InvestmentsGoalsList,
  LegalEntityTaxAgentList,
  LegalEntityTaxAgentType,
  PerspectivesList,
  PublicFundTaxAgentList,
  PublicFundTaxAgentType,
  TaxAgentList,
  TaxAgentType,
  TaxAgentWithBrokerList,
  TaxBurdenList,
} from '@/shared/lib/sj-orm/models/assets';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';
import { CollectionName } from '@/shared/lib/sj-orm/types';

type AssetsStatisticsHelpPart = {
  taxBurden?: QuestionType.SELECT_FROM_LIST;
  taxAgent?: QuestionType.SELECT_FROM_LIST;
  publicFundTaxAgent?: QuestionType.SELECT_FROM_LIST;
  legalTaxAgent?: QuestionType.SELECT_FROM_LIST;
  holderTaxAgent?: QuestionType.SELECT_FROM_LIST;
  taxAgentWithBroker?: QuestionType.SELECT_FROM_LIST;
  taxAgentOther?: QuestionType.TEXT;
  publicTaxAgentOther?: QuestionType.TEXT;
  legalTaxAgentOther?: QuestionType.TEXT;
  holderTaxAgentOther?: QuestionType.TEXT;
  taxAgentWithBrokerOther?: QuestionType.TEXT;
  insurance: QuestionType.CHECKBOX;
  insuranceDocument: QuestionType.PICK_ARRAY_OF_DTO;
  liquidity?: QuestionType.SELECT_FROM_LIST;
  activity?: QuestionType.SELECT_FROM_LIST;
  investmentsGoals?: QuestionType.SELECT_FROM_LIST;
  outlook?: QuestionType.SELECT_FROM_LIST;
  riskRate?: QuestionType.SELECT_FROM_LIST;
  informationDisclosure?: QuestionType.SELECT_FROM_LIST;
  informationDisclosureDetails?: QuestionType.TEXT_MULTILINE;
  specificsOfTaxation?: QuestionType.TEXT_MULTILINE;
  profitability?: QuestionType.CURRENCY_CALCULATOR;
  profitabilityFact?: QuestionType.CURRENCY_CALCULATOR;
  profitabilityPlan?: QuestionType.CURRENCY_CALCULATOR;
  commentsOnTheAsset?: QuestionType.TEXT_MULTILINE;
  limitations?: QuestionType.CHECKBOX;
  limitationsComments?: QuestionType.TEXT_MULTILINE;
};

export type AssetsStatisticsPartType = TDtoWrapper<AssetsStatisticsPart, AssetsStatisticsHelpPart>;

export const AssetsStatisticsFormPartFull: AssetsStatisticsPartType = {
  specificsOfTaxation: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_assets:common.inputs.specificsOfTaxation.label',
    order: 1,
  },
  taxBurden: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.taxBurden.label',
    items: TaxBurdenList,
    order: 2,
    tooltip: 'forms_assets:common.inputs.taxBurden.tooltip',
  },
  taxAgent: {
    type: QuestionType.SELECT_FROM_LIST,
    // eslint-disable-next-line sonarjs/no-duplicate-string
    label: 'forms_assets:common.inputs.taxAgent.label',
    items: TaxAgentList,
    order: 3,
  },
  taxAgentWithBroker: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.taxAgent.label',
    items: TaxAgentWithBrokerList,
    order: 3,
  },
  publicFundTaxAgent: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.taxAgent.label',
    items: PublicFundTaxAgentList,
    order: 3,
  },
  legalTaxAgent: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.taxAgent.label',
    items: LegalEntityTaxAgentList,
    order: 3,
  },
  holderTaxAgent: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.taxAgent.label',
    items: HolderTaxAgentList,
    order: 3,
  },
  taxAgentOther: {
    type: QuestionType.TEXT,
    // eslint-disable-next-line sonarjs/no-duplicate-string
    label: 'forms_assets:common.inputs.otherTaxAgent.label',
    showIf: {
      taxAgent: {
        value: TaxAgentType.other,
      },
    },
    order: 4,
  },
  publicTaxAgentOther: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.otherTaxAgent.label',
    showIf: {
      publicFundTaxAgent: {
        value: PublicFundTaxAgentType.other,
      },
    },
    order: 4,
  },
  taxAgentWithBrokerOther: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.otherTaxAgent.label',
    showIf: {
      taxAgentWithBroker: {
        value: PublicFundTaxAgentType.other,
      },
    },
    order: 4,
  },
  legalTaxAgentOther: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.otherTaxAgent.label',
    showIf: {
      legalTaxAgent: {
        value: LegalEntityTaxAgentType.other,
      },
    },
    order: 4,
  },
  holderTaxAgentOther: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.otherTaxAgent.label',
    showIf: {
      holderTaxAgent: {
        value: HolderTaxAgentType.other,
      },
    },
    order: 4,
  },
  insurance: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:jewelery.inputs.insurance.label',
    order: 7,
  },
  insuranceDocument: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:jewelery.inputs.insuranceDocument.label',
    items: CollectionName.PRIVATE_DOCUMENTS,
    showIf: {
      insurance: {
        value: true,
      },
    },
    // dtoType: PrivateDocumentCategory.OTHER,
    order: 8,
  },
  liquidity: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.liquidity.label',
    items: HighMediumLowList,
    order: 11,
    tooltip: 'forms_assets:common.inputs.liquidity.tooltip',
  },
  activity: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.activity.label',
    tooltip: 'forms_assets:common.inputs.activity.tooltip',
    items: ActivityList,
    order: 12,
  },
  investmentsGoals: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.goal.label',
    tooltip: 'forms_assets:common.inputs.goal.tooltip',
    items: InvestmentsGoalsList,
    order: 13,
  },
  outlook: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.outlook.label',
    tooltip: 'forms_assets:common.inputs.outlook.tooltip',
    items: PerspectivesList,
    order: 14,
  },
  riskRate: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.riskRate.label',
    items: HighMediumLowList,
    order: 15,
    tooltip: 'forms_assets:common.inputs.riskRate.tooltip',
  },
  informationDisclosure: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.informationDisclosure.label',
    tooltip: 'forms_assets:common.inputs.informationDisclosure.tooltip',
    items: InformationDisclosureList,
    order: 16,
  },
  informationDisclosureDetails: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_assets:common.inputs.informationDisclosureDetails.label',
    order: 17,
  },
  // profitability: {
  //   type: QuestionType.CURRENCY_CALCULATOR,
  //   label: 'forms_assets:common.inputs.profitability.label',
  //   tooltip: 'forms_assets:common.inputs.profitability.tooltip',
  //   order: 18,
  // },
  // profitabilityPlan: {
  //   type: QuestionType.CURRENCY_CALCULATOR,
  //   label: 'forms_assets:common.inputs.profitabilityPlan.label',
  //   order: 19,
  // },
  // profitabilityFact: {
  //   type: QuestionType.CURRENCY_CALCULATOR,
  //   label: 'forms_assets:common.inputs.profitabilityFact.label',
  //   order: 20,
  // },
  commentsOnTheAsset: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_assets:common.inputs.commentsOnTheAsset.label',
    order: 21,
    colSpan: 2,
  },
  limitations: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:common.inputs.limitations.label',
    order: 22,
    tooltip: 'forms_assets:common.inputs.limitations.tooltip',
  },
  limitationsComments: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_assets:common.inputs.limitationsComments.label',
    showIf: {
      limitations: {
        value: true,
      },
    },
    order: 24,
  },
};

export const assetsStatisticsBuilder = (excludeItems?: Array<keyof AssetsStatisticsPart>) => {
  if (!Array.isArray(excludeItems) || !excludeItems.length) {
    return {
      form: AssetsStatisticsFormPartFull,
    };
  }

  const copy = Object.assign({}, AssetsStatisticsFormPartFull);

  let cursor = 0;
  const length = excludeItems.length;

  while (cursor < length) {
    delete copy[excludeItems[cursor]];
    cursor++;
  }

  return {
    form: copy,
  };
};
